import { useEffect, useMemo, useState } from 'react';
import { CaretLeft, CaretRight, Tag } from '@phosphor-icons/react';

import { BottomSheet, BottomSheetContent } from '../common/BottomSheet';
import { Button } from '../ui/Button';
import { categoryLabel, categoryMeta } from '../accounts/assetCategories';
import { QuickCreatePickerRow } from './QuickCreatePickerRow';
import { cn } from '../../utils/cn';
import pt from '../../locales/pt';
import { formatCategoryLimitLabel } from '../../utils/categoryAllocation';
import { categoryIdsMatch } from '../categories/categoryPickerUtils';
import { resolveIncomeSourceIcon } from '../../utils/incomeSourceIcons';
import {
  buildMonthGrid,
  clampISODate,
  formatQuickCalendarMonth,
  isISODateDisabled,
  shiftMonth,
  todayISO,
} from '../../utils/quickCreateDates';

export function AccountPickerSheet({ open, onOpenChange, accounts, selectedId, onSelect }) {
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent title={pt.quickCreate.accountPickerTitle} layout="bottom">
        <ul className="flex flex-col gap-space-2 pb-4">
          {accounts.map((account) => {
            const meta = categoryMeta(account.category);
            return (
              <li key={account.id}>
                <QuickCreatePickerRow
                  icon={meta.icon}
                  name={account.name}
                  subtitle={categoryLabel(account.category)}
                  selected={categoryIdsMatch(account.id, selectedId)}
                  onSelect={() => {
                    onSelect(String(account.id));
                    onOpenChange(false);
                  }}
                />
              </li>
            );
          })}
        </ul>
      </BottomSheetContent>
    </BottomSheet>
  );
}

export function CategoryPickerSheet({ open, onOpenChange, categories, selectedId, onSelect, onAddNew }) {
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent title={pt.quickCreate.categoryPickerTitle} layout="bottom">
        <ul className="flex flex-col gap-space-2 pb-4">
          {categories.map((category) => (
            <li key={category.id}>
              <QuickCreatePickerRow
                icon={Tag}
                name={category.name}
                subtitle={formatCategoryLimitLabel(category, pt)}
                selected={categoryIdsMatch(category.id, selectedId)}
                onSelect={() => {
                  onSelect(String(category.id));
                  onOpenChange(false);
                }}
              />
            </li>
          ))}
        </ul>
        {onAddNew ? (
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              onOpenChange(false);
              onAddNew();
            }}
            className="mb-2 w-full justify-center"
          >
            {pt.quickCreate.newCategory}
          </Button>
        ) : null}
      </BottomSheetContent>
    </BottomSheet>
  );
}

export function SourcePickerSheet({ open, onOpenChange, sources, selectedId, onSelect, onAddNew }) {
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent title={pt.quickCreate.sourcePickerTitle} layout="bottom">
        <ul className="flex flex-col gap-space-2 pb-4">
          {sources.map((source) => (
            <li key={source.id}>
              <QuickCreatePickerRow
                icon={resolveIncomeSourceIcon(source)}
                name={source.name}
                selected={categoryIdsMatch(source.id, selectedId)}
                onSelect={() => {
                  onSelect(String(source.id));
                  onOpenChange(false);
                }}
              />
            </li>
          ))}
        </ul>
        {onAddNew ? (
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              onOpenChange(false);
              onAddNew();
            }}
            className="mb-2 w-full justify-center"
          >
            {pt.quickCreate.newSource}
          </Button>
        ) : null}
      </BottomSheetContent>
    </BottomSheet>
  );
}

const RECURRENCE_OPTIONS = [
  { value: 'monthly_fixed', labelKey: 'monthlyFixed' },
  { value: 'monthly_variable', labelKey: 'monthlyVariable' },
  { value: 'occasional', labelKey: 'occasional' },
];

export function RecurrencePickerSheet({ open, onOpenChange, value, onSelect }) {
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent title={pt.quickCreate.recurrenceLabel} layout="bottom">
        <ul className="flex flex-col gap-1 pb-4">
          {RECURRENCE_OPTIONS.map((option) => {
            const selected = value === option.value;
            return (
              <li key={option.value}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(option.value);
                    onOpenChange(false);
                  }}
                  className={cn(
                    'flex w-full items-center rounded-xl px-3 py-3 text-left text-sm transition-colors',
                    selected ? 'bg-sage-50 font-semibold text-sage-800 dark:bg-sage-950/40' : 'hover:bg-muted',
                  )}
                >
                  {pt.quickCreate.recurrence[option.labelKey]}
                </button>
              </li>
            );
          })}
        </ul>
      </BottomSheetContent>
    </BottomSheet>
  );
}

export function recurrenceLabel(value) {
  const option = RECURRENCE_OPTIONS.find((item) => item.value === value);
  return option ? pt.quickCreate.recurrence[option.labelKey] : '';
}

/**
 * In-sheet calendar — avoids native `<input type="date">` inside Radix Dialog
 * (UA picker is unusable with modal pointer-events / outside dismiss).
 */
export function DatePickerSheet({ open, onOpenChange, value, min, max, onSelect }) {
  const initial = clampISODate(value || todayISO(), min, max);
  const seed = initial ? new Date(`${initial}T12:00:00`) : new Date();
  const [cursor, setCursor] = useState({
    year: seed.getFullYear(),
    monthIndex: seed.getMonth(),
  });

  useEffect(() => {
    if (!open) return;
    const next = clampISODate(value || todayISO(), min, max);
    const date = next ? new Date(`${next}T12:00:00`) : new Date();
    setCursor({ year: date.getFullYear(), monthIndex: date.getMonth() });
  }, [open, value, min, max]);

  const cells = useMemo(
    () => buildMonthGrid(cursor.year, cursor.monthIndex),
    [cursor.year, cursor.monthIndex],
  );

  const monthLabel = formatQuickCalendarMonth(cursor.year, cursor.monthIndex);
  const today = todayISO();

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent title={pt.quickCreate.datePickerTitle} layout="bottom">
        <div className="flex flex-col gap-3 pb-4">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setCursor((current) => shiftMonth(current.year, current.monthIndex, -1))}
              aria-label={pt.quickCreate.datePrevMonth}
              className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <CaretLeft className="h-5 w-5" aria-hidden />
            </button>
            <p className="min-w-0 flex-1 truncate text-center text-sm font-semibold capitalize text-foreground">
              {monthLabel}
            </p>
            <button
              type="button"
              onClick={() => setCursor((current) => shiftMonth(current.year, current.monthIndex, 1))}
              aria-label={pt.quickCreate.dateNextMonth}
              className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <CaretRight className="h-5 w-5" aria-hidden />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {pt.quickCreate.dateWeekdays.map((weekday) => (
              <span
                key={weekday}
                className="py-1 text-center text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
              >
                {weekday}
              </span>
            ))}
            {cells.map((cell) => {
              const disabled = isISODateDisabled(cell.iso, min, max);
              const selected = cell.iso === value;
              const isToday = cell.iso === today;
              return (
                <button
                  key={cell.iso}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    onSelect(cell.iso);
                    onOpenChange(false);
                  }}
                  aria-label={cell.iso}
                  aria-pressed={selected}
                  className={cn(
                    'flex h-10 items-center justify-center rounded-xl text-sm transition-colors',
                    !cell.inMonth && 'text-muted-foreground/50',
                    cell.inMonth && !selected && 'text-foreground hover:bg-muted',
                    isToday && !selected && 'font-semibold text-sage-700',
                    selected && 'bg-sage-600 font-semibold text-white hover:bg-sage-600',
                    disabled && 'cursor-not-allowed opacity-30 hover:bg-transparent',
                  )}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>
        </div>
      </BottomSheetContent>
    </BottomSheet>
  );
}
